import { RoundService } from '@/services/RoundService';
import { APP_CONFIG } from '../constants';


export class BackgroundSyncService {
    private syncInterval: NodeJS.Timeout | null = null;
    private isSyncing: boolean = false;
    private lastSyncTime: number = 0;
    private syncQueue: Set<string> = new Set(); // Set of round IDs to sync
    private roundService: RoundService;

    constructor(roundService: RoundService) {
        // Start the sync service
        this.startSync();
        this.roundService = roundService;
    }

    private async syncRound(roundId: string): Promise<boolean> {
        try {
            const localData = await this.roundService.get(roundId);
            if (!localData || localData.id !== roundId) return true;

            const userId = (Object.keys(localData.users).length > 0 ? Object.values(localData.users)[0].uid : null);

            await this.roundService.update(roundId, userId, localData, null);
            this.syncQueue.delete(roundId);
            this.lastSyncTime = Date.now();
            return true;
        } catch (error) {
            console.error('Error syncing round:', error);
            return false;
        }
    }

    private async processSyncQueue(): Promise<void> {
        if (this.isSyncing || this.syncQueue.size === 0) return;

        this.isSyncing = true;
        try {
            for (const roundId of this.syncQueue) {
                await this.syncRound(roundId);
            }
        } finally {
            this.isSyncing = false;
        }
    }

    public startSync(): void {
        if (this.syncInterval) return;

        this.syncInterval = setInterval(() => {
            this.processSyncQueue();
        }, APP_CONFIG.SYNC_INTERVAL);
    }

    public stopSync(): void {
        if (this.syncInterval) {
            clearInterval(this.syncInterval);
            this.syncInterval = null;
        }
    }

    public queueSync(roundId: string): void {
        // Only queue if enough time has passed since last sync
        if (Date.now() - this.lastSyncTime >= APP_CONFIG.MIN_SYNC_INTERVAL) {
            this.syncQueue.add(roundId);
        }
    }

    public async forceSync(roundId: string): Promise<boolean> {
        return await this.syncRound(roundId);
    }

    public isRoundSynced(roundId: string): boolean {
        return !this.syncQueue.has(roundId);
    }
}
